
import Vue from 'vue'
import * as Excel from 'exceljs'
import { saveAs } from 'file-saver'
import {
  formatDateValue,
  formatDisplayDateTime,
  validDate,
} from '@/util/formatters'
import { noDataText, trimArray } from '@/util/tableUtilities'

import JcdcExportWrapper from '@/components/jcdc/JcdcExportWrapper.vue'
import {
  ExcelAppendItem,
  JcdcTableHeader,
  PrependItem,
  items,
} from '@/util/tableUtilities'

// Should match AppendItem in JcdcDataTable

export default Vue.extend({
  name: 'JcdcExportExcel',
  components: {
    JcdcExportWrapper,
  },
  props: {
    // Data from Footer
    infoDate: { type: [String, Date, Number, Object], default: null },
    // Data from Brand Bar
    appTitle: { type: String, required: true },
    appSubtitle: { type: String, required: true },
    // Data from Page Heading
    title: { type: String, required: true },
    tabTitle: { type: String, default: 'Report' },
    searchList: { type: Array as () => string[], default: (): [] => [] },
    filterList: { type: Array as () => string[], default: (): [] => [] },
    dataList: { type: Array as () => string[], default: (): [] => [] },
    // Data from Data Table - Use Mobile Headers
    headers: { type: Array as () => JcdcTableHeader[], required: true },
    items: { type: Array as () => items[], required: true },
    prependHeader: {
      type: Array as () => PrependItem[][],
      default: (): [] => [],
    },
    // NEW
    totalRow: { type: Array as () => ExcelAppendItem[], default: undefined },
    groupRows: { type: Array as () => ExcelAppendItem[][], default: undefined },
    groupText: { type: String, default: 'Row Groups' },
    // Button
    align: { type: String, default: 'left' },
    secondary: { type: Boolean, default: false },
    iconOnly: { type: Boolean, default: false },
    btnText: { type: String, default: 'Export Excel' },
    //
    dataOnly: { type: Boolean, default: false },
    // Custom btn
    customBtn: { type: Boolean, default: false },
    // No Data
    noDataText: { type: String, default: noDataText },
  },
  data() {
    return {
      loading: false,
    }
  },
  computed: {
    attachID() {
      return `_${Math.random().toString(36).substr(2, 9)}`
    },
    // Find Dates
    dateCellValues() {
      return this.headers
        .filter((row) =>
          ['date', 'dateTime'].includes(row.formatter ? row.formatter : '')
        )
        .map((x) => x.value)
    },
    timeCellValues() {
      return this.headers
        .filter((row) => ['time'].includes(row.formatter ? row.formatter : ''))
        .map((x) => x.value)
    },
    booleanCellValues() {
      return this.headers
        .filter((row) =>
          ['boolean'].includes(row.formatter ? row.formatter : '')
        )
        .map((x) => x.value)
    },
  },
  methods: {
    formatDisplayDateTime,
    formatDateValue,
    trimArray,
    validDate,
    formattedCustomRow(row, type?) {
      const rowTemplate = {}
      this.headers.map((head) => {
        const cellValue = row.find((cell) => cell.value == head.value),
          obj = {
            [head.value]: cellValue
              ? cellValue.text
              : type === 'data'
              ? null
              : '',
          }
        Object.assign(rowTemplate, obj)
      })
      return rowTemplate
    },
    clickTimeout(fileName) {
      // Used to give the data table a moment to display all before initiating Excel build. -MN
      this.loading = true
      this.$emit('click', true)
      setTimeout(() => {
        this.buildExcel(fileName)
      }, 100)
    },
    convertToLetter(item) {
      return String.fromCharCode(96 + item).toUpperCase()
    },
    headerStyles(cell) {
      cell.font = { bold: true, color: { argb: '00FFFFFF' } }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '000071bc' },
      }
      cell.border = {
        right: { style: 'thin', color: { argb: '00FFFFFF' } },
        bottom: { style: 'thin', color: { argb: '00FFFFFF' } },
      }
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
        wrapText: true,
      }
    },
    groupHeaderStyles(worksheet, cell, end, text) {
      worksheet.mergeCells(`${cell}:${end}`)
      worksheet.getCell(cell).value = text
      worksheet.getCell(cell).font = { size: 12, bold: true }
      worksheet.getCell(cell).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '00dddddd' },
      }
    },
    numberFormat(formatter, digits) {
      const defaultNone =
          digits == undefined || digits == 0 ? '' : `.${'0'.repeat(digits)}`,
        defaultTwo =
          digits == undefined
            ? '.00'
            : digits == 0
            ? ''
            : `.${'0'.repeat(digits)}`
      switch (formatter) {
        case 'number':
          return `#,##0${defaultNone}`
        case 'average':
          return '#,##0.00'
        case 'percent':
          return `0${defaultTwo}%`
        case 'currency':
          return `$#,##0${defaultTwo}_);($#,##0${defaultTwo})`
        case 'date':
          return 'mm/dd/yyyy;@'
        case 'dateTime':
          return 'mm/dd/yy hh:mm AM/PM;@'
        case 'phone':
          return '[<=9999999]###-####;(###) ###-####'
        case 'time':
          return 'h:mm AM/PM'
        case 'fileSize':
          return '[<1024]##0.00" B";[<1048576]##0.00," KB";##0.00,," MB"'
        case 'zip':
          return '00000'
        default:
          return 'General'
      }
    },
    subHeaderFormat(worksheet, cell, end, items) {
      worksheet.mergeCells(`${cell}:${end}`)
      worksheet.getCell(cell).value = items
        ? `${this.trimArray(items).join(' | ')}`
        : ''
      worksheet.getCell(cell).font = { size: 12, bold: true }
    },
    parseData(row) {
      const parsedRow = { ...row }
      if (this.dateCellValues.length > 0) {
        this.dateCellValues.forEach((dateCell) => {
          const value = parsedRow[dateCell]
          if (value) {
            const obj = {
              [dateCell]: this.validDate(value)
                ? Date.parse(value.toString()) / 86400000 + 25569 - 5 / 24
                : undefined,
            }
            Object.assign(parsedRow, obj)
          }
        })
      }
      if (this.timeCellValues.length > 0) {
        this.timeCellValues.forEach((timeCell) => {
          const value = parsedRow[timeCell]
          if (value) {
            const obj = {
              [timeCell]:
                Date.parse(`01 Jan 1970 ${value.toString()}`) / 86400000 -
                6 / 24,
            }
            Object.assign(parsedRow, obj)
          }
        })
      }
      if (this.booleanCellValues.length > 0) {
        this.booleanCellValues.forEach((boolCell) => {
          const value = parsedRow[boolCell]
          if (value) {
            const obj = {
              [boolCell]:
                value === true || value === 'Y' || value === 'Yes'
                  ? true
                  : value === false || value === 'N' || value === 'No'
                  ? false
                  : '',
            }
            Object.assign(parsedRow, obj)
          }
        })
      }
      return parsedRow
    },
    async buildExcel(fileName) {
      // Tab Title
      let newTab
      this.dataOnly && this.tabTitle == 'Report'
        ? (newTab = this.title)
        : (newTab = this.tabTitle)

      // Create workbook and add worksheet
      const workbook = new Excel.Workbook(),
        worksheet = workbook.addWorksheet(newTab)
      let currentRowNo = 1
      const headerMergeLength = this.convertToLetter(this.headers.length)

      if (!this.dataOnly) {
        // Format Row 1 - Report Title
        const currentCell = `A${currentRowNo}`
        worksheet.mergeCells(
          `${currentCell}:${headerMergeLength}${currentRowNo}`
        )
        worksheet.getCell(currentCell).value = this.title
        worksheet.getCell(currentCell).font = { size: 18, bold: true }
        currentRowNo++
        // Format Row 2 - Search List
        if (this.searchList.length != 0) {
          this.subHeaderFormat(
            worksheet,
            `A${currentRowNo}`,
            `${headerMergeLength}${currentRowNo}`,
            this.searchList
          )
          currentRowNo++
        }
        // Format Row 3 - Filter List
        if (this.filterList.length != 0) {
          this.subHeaderFormat(
            worksheet,
            `A${currentRowNo}`,
            `${headerMergeLength}${currentRowNo}`,
            this.filterList
          )
          currentRowNo++
        }
        // Format Row 4 - Data List
        if (this.dataList.length != 0) {
          this.subHeaderFormat(
            worksheet,
            `A${currentRowNo}`,
            `${headerMergeLength}${currentRowNo}`,
            this.dataList
          )
          currentRowNo++
        }
        // Blank Row
        currentRowNo++
        // Prepend Headers
        if (this.prependHeader.length != 0) {
          const headers = this.prependHeader,
            rowSpanIndex: Array<string> = []
          headers.forEach((row) => {
            let cellIndex = 1
            // ROW
            row.forEach((cell) => {
              // Skip row spanned cells
              if (rowSpanIndex.length > 0) {
                rowSpanIndex.forEach((spannedCell) => {
                  if (
                    spannedCell ===
                    `${this.convertToLetter(cellIndex)}:${currentRowNo}`
                  ) {
                    cellIndex++
                  }
                })
              }
              // Colspan
              const colSpan = cell.colspan ? cell.colspan : 1,
                rowSpan = cell.rowspan ? cell.rowspan : 1,
                cellStart = this.convertToLetter(cellIndex) + currentRowNo

              for (let col = 0; col < colSpan; col++) {
                for (let row = 1; row < rowSpan; row++) {
                  rowSpanIndex.push(
                    `${this.convertToLetter(cellIndex)}:${currentRowNo + row}`
                  )
                }
                cellIndex++
              }
              const cellEnd =
                this.convertToLetter(cellIndex - 1) +
                (currentRowNo + rowSpan - 1)
              worksheet.mergeCells(`${cellStart}:${cellEnd}`)
              worksheet.getCell(cellStart).value = cell.title
            })
            worksheet
              .getRow(currentRowNo)
              .eachCell((cell) => this.headerStyles(cell))
            currentRowNo++
          })
        }
        // Freeze headers
        worksheet.views = [
          {
            state: 'frozen',
            ySplit: currentRowNo,
          },
        ]
      }

      // Build worksheet Columns
      worksheet.columns = this.headers.map((x) => {
        const numFmt = this.numberFormat(x.formatter, x.digits)
        return {
          key: x.value,
          style: { numFmt },
          width: x.text.length < 18 ? 18 : x.text.length,
        }
      })

      // Set header row values
      worksheet.getRow(currentRowNo).values = this.headers.map((x) => x.text)
      // Format header row
      worksheet.getRow(currentRowNo).eachCell((cell) => this.headerStyles(cell))
      currentRowNo++

      // Add Grouped Rows
      if (!this.dataOnly && this.groupRows) {
        // Add Row Group Header
        this.groupHeaderStyles(
          worksheet,
          `A${currentRowNo}`,
          `${headerMergeLength}${currentRowNo}`,
          this.groupText
        )
        currentRowNo++
        // Find row group formats
        const groupFormats = this.headers.map((head) => ({
          ...head,
          ...this.groupRows[0].find((group) => group.value === head.value),
        }))
        // Add Grouped Rows
        this.groupRows.forEach((group) => {
          const formatRow = this.formattedCustomRow(group),
            parsedRow = this.parseData(formatRow)
          worksheet.addRow(parsedRow)
          // Format numbers
          groupFormats.forEach((cell, index) => {
            worksheet.getCell(
              `${this.convertToLetter(index + 1)}${currentRowNo}`
            ).numFmt = this.numberFormat(cell.formatter, cell.digits)
          })
          currentRowNo++
        })
        // Add blank rows
        currentRowNo++
        // Add Table Group Header
        this.groupHeaderStyles(
          worksheet,
          `A${currentRowNo}`,
          `${headerMergeLength}${currentRowNo}`,
          'Table Data'
        )
        currentRowNo++
      }
      // Add worksheet row for every row in dataToUse
      this.items.forEach((row) => {
        const parsedRow = this.parseData(row)
        worksheet.addRow(parsedRow)
        currentRowNo++
      })

      // Formatting colum 1
      worksheet.getColumn(1).alignment = {
        vertical: 'middle',
        horizontal: 'left',
      }

      // NO DATA
      if (this.items.length == 0) {
        const currentCell = `A${currentRowNo}`,
          mergeEndCell = `${headerMergeLength}${currentRowNo + 1}`
        worksheet.mergeCells(`${currentCell}:${mergeEndCell}`)
        worksheet.getCell(currentCell).value = this.noDataText
        // Format Row
        worksheet.lastRow?.eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: '00F3F3F4' },
          }
          cell.alignment = { vertical: 'middle', horizontal: 'center' }
        })
        currentRowNo = currentRowNo + 2
      }

      if (!this.dataOnly) {
        // TOTAL ROW
        if (this.totalRow) {
          // Find cell formats
          const totalFormats = this.headers.map((head) => ({
              ...head,
              ...this.totalRow.find((cell) => cell.value === head.value),
            })),
            formatRow = this.formattedCustomRow(this.totalRow),
            parsedRow = this.parseData(formatRow)
          // Add Row
          worksheet.addRow(parsedRow)
          // Format numbers
          totalFormats.forEach((cell, index) => {
            worksheet.getCell(
              `${this.convertToLetter(index + 1)}${currentRowNo}`
            ).numFmt = this.numberFormat(cell.formatter, cell.digits)
          })
          // Format styles
          worksheet.lastRow?.eachCell((cell) => {
            cell.font = { bold: true }
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: '00dddddd' },
            }
            cell.border = {
              top: { style: 'thin', color: { argb: '000071bc' } },
            }
          })
          currentRowNo++
        }
        // PAGE FOOTER
        // Blank Row
        currentRowNo++
        // Message Bottom
        const currentCell = `A${currentRowNo}`
        worksheet.mergeCells(
          `${currentCell}:${headerMergeLength}${currentRowNo}`
        )
        worksheet.getCell(currentCell).value = `${
          this.appSubtitle
        } - ${this.formatDisplayDateTime(
          this.infoDate ? this.infoDate : new Date()
        )}`
        // Format Row
        worksheet.lastRow?.eachCell(function (cell) {
          cell.font = { italic: true, color: { argb: '00999999' } }
        })
      }

      // Save workbook
      const buffer = await workbook.xlsx.writeBuffer(),
        fileType =
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        blob = new Blob([buffer], { type: fileType })
      saveAs(blob, `${fileName}.xlsx`)
      //
      this.loading = await false
      await this.$emit('complete', true)
    },
  },
})
